import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, MessageSquare, Info, Camera, Bike, ArrowLeft, Calendar, ChevronDown, ChevronUp, Trash2, DollarSign } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { FileUpload } from './FileUpload';
import { FileViewer } from './FileViewer';
import { PhotoCapture } from './PhotoCapture';

const formatAmount = (value: string): string => {
  const numericValue = value.replace(/[^\d.]/g, '');
  
  const parts = numericValue.split('.');
  const wholePart = parts[0];
  const decimalPart = parts[1]?.slice(0, 2) || '00';
  
  return `${wholePart || '0'}.${decimalPart}`;
};

const AmountInput = ({ value, onChange, increment = 100, label }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatAmount(e.target.value);
    onChange(formattedValue);
  };

  const adjustAmount = (adjustment: number) => {
    const currentAmount = parseFloat(value) || 0;
    const newAmount = Math.max(0, currentAmount + adjustment);
    onChange(newAmount.toFixed(2));
  };

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="flex items-center">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-500">$</span>
          </div>
          <input
            type="text"
            value={value}
            onChange={handleChange}
            className="w-full pl-7 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6563ff] focus:border-[#6563ff]"
            placeholder="0.00"
          />
          <div className="absolute inset-y-0 right-0 flex flex-col border-l border-gray-300">
            <button
              type="button"
              onClick={() => adjustAmount(increment)}
              className="flex-1 px-2 hover:bg-gray-100 transition-colors"
            >
              <ChevronUp className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => adjustAmount(-increment)}
              className="flex-1 px-2 hover:bg-gray-100 transition-colors"
            >
              <ChevronDown className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const STATUS_ORDER = [
  'new_matter',
  'customer_contacted', 
  'awaiting_approval',
  'bike_delivered',
  'bike_returned',
  'demands_sent',
  'awaiting_settlement',
  'settlement_agreed',
  'paid',
  'closed'
];

export function CaseDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('updates');
  const [caseData, setCaseData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [showPhotoCapture, setShowPhotoCapture] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingNote, setEditingNote] = useState(null);
  const [showReturnConfirmation, setShowReturnConfirmation] = useState(false);
  const [showReserveInput, setShowReserveInput] = useState(false);
  const [showSettlementInput, setShowSettlementInput] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [reserveAmount, setReserveAmount] = useState('0.00');
  const [settlementAmount, setSettlementAmount] = useState('0.00');
  const [statusError, setStatusError] = useState<string | null>(null);

  useEffect(() => {
    fetchCaseData();
    fetchDocuments();
  }, [id]);

  const fetchCaseData = async () => {
    try {
      const { data, error } = await supabase
        .from('cases')
        .select(`
          *,
          bike:bikes(
            id,
            model,
            rego_number,
            rate_a,
            rate_b,
            status
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      setCaseData(data);
      if (data.reserve_amount) {
        setReserveAmount(data.reserve_amount.toString());
      }
      if (data.settlement_amount) {
        setSettlementAmount(data.settlement_amount.toString());
      }
    } catch (error) {
      console.error('Error fetching case:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('case_id', id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      const note = {
        timestamp: new Date().toISOString(),
        content: newNote.trim(),
        type: 'follow_up'
      };

      const { data: currentCase } = await supabase
        .from('cases')
        .select('notes')
        .eq('id', id)
        .single();

      const updatedNotes = [note, ...(currentCase?.notes || [])];

      const { error } = await supabase
        .from('cases')
        .update({ notes: updatedNotes })
        .eq('id', id);

      if (error) throw error;

      setNewNote('');
      fetchCaseData();
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  const handleUpdateNoteTimestamp = async (noteIndex, newTimestamp) => {
    try {
      const updatedNotes = [...caseData.notes];
      updatedNotes[noteIndex] = {
        ...updatedNotes[noteIndex],
        timestamp: newTimestamp
      };

      const { error } = await supabase
        .from('cases')
        .update({ notes: updatedNotes })
        .eq('id', id);

      if (error) throw error;
      fetchCaseData();
      setEditingNote(null);
    } catch (error) {
      console.error('Error updating note timestamp:', error);
    }
  };

  const handleUpdateDates = async (startDate, endDate) => {
    try {
      const { error } = await supabase
        .from('cases')
        .update({
          rental_start_date: startDate,
          rental_end_date: endDate
        })
        .eq('id', id);

      if (error) throw error;
      fetchCaseData();
    } catch (error) {
      console.error('Error updating dates:', error);
    }
  };

  const handleStatusChange = async (newStatus) => {
    const currentIndex = STATUS_ORDER.indexOf(caseData.status);
    const newIndex = STATUS_ORDER.indexOf(newStatus);

    if (newIndex !== currentIndex + 1) {
      setStatusError(`Cannot change to ${formatStatus(newStatus)}. Please proceed through the states in order.`);
      return;
    }

    setStatusError(null);

    try {
      if (newStatus === 'demands_sent') {
        const rateBTotal = (caseData.total_days || 0) * (caseData.bike?.rate_b || 0);
        setSettlementAmount(rateBTotal.toFixed(2));
        setShowSettlementInput(true);
        return;
      }

      if (newStatus === 'awaiting_settlement') {
        setShowReserveInput(true);
        return;
      }

      if (newStatus === 'settlement_agreed') {
        setShowSettlementInput(true);
        return;
      }

      if (newStatus === 'bike_returned') {
        setShowReturnConfirmation(true);
        return;
      }

      const { error } = await supabase
        .from('cases')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;
      fetchCaseData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleReserveAmountSubmit = async () => {
    try {
      const { error } = await supabase
        .from('cases')
        .update({
          status: 'awaiting_settlement',
          reserve_amount: parseFloat(reserveAmount)
        })
        .eq('id', id);

      if (error) throw error;
      setShowReserveInput(false);
      fetchCaseData();
    } catch (error) {
      console.error('Error updating reserve amount:', error);
    }
  };

  const handleSettlementAmountSubmit = async () => {
    try {
      const { error } = await supabase
        .from('cases')
        .update({
          status: 'demands_sent',
          settlement_amount: parseFloat(settlementAmount),
          current_amount: parseFloat(settlementAmount)
        })
        .eq('id', id);

      if (error) throw error;
      setShowSettlementInput(false);
      fetchCaseData();
    } catch (error) {
      console.error('Error updating settlement amount:', error);
    }
  };

  const handleConfirmReturn = async () => {
    try {
      const { error } = await supabase
        .from('cases')
        .update({
          status: 'bike_returned',
          rental_end_date: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;
      fetchCaseData();
      setShowReturnConfirmation(false);
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDeleteCase = async () => {
    try {
      const { error } = await supabase
        .from('cases')
        .delete()
        .eq('id', id);

      if (error) throw error;
      navigate('/cases');
    } catch (error) {
      console.error('Error deleting case:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#6563ff]"></div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Case Not Found</h2>
          <p className="text-gray-600 mb-4">The case you're looking for doesn't exist or has been deleted.</p>
          <button
            onClick={() => navigate('/cases')}
            className="text-[#6563ff] hover:text-[#5451ff]"
          >
            ← Back to Cases
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/cases')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Cases
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            Case {caseData.case_number}
          </h1>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowDeleteConfirmation(true)}
            className="flex items-center px-4 py-2 text-red-600 hover:text-red-700 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
          >
            <Trash2 className="h-5 w-5 mr-2" />
            Delete Case
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-[#6563ff] p-6 text-white">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <h3 className="text-sm font-medium text-white/80">Client Name</h3>
              <p className="text-lg font-semibold">{caseData?.not_at_fault_name || '-'}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-white/80">Insurance Company</h3>
              <p className="text-lg font-semibold">{caseData?.not_at_fault_insurance_company || '-'}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-white/80">Claim Number</h3>
              <p className="text-lg font-semibold">{caseData?.claim_number || '-'}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-white/80">Reference Number</h3>
              <p className="text-lg font-semibold">{caseData?.reference_number || '-'}</p>
            </div>
          </div>
        </div>

        <div className="border-b border-gray-200 p-6 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Start Date</label>
              <input
                type="datetime-local"
                value={caseData?.rental_start_date?.slice(0, 16) || ''}
                onChange={(e) => handleUpdateDates(e.target.value, caseData?.rental_end_date)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#6563ff] focus:ring-[#6563ff]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">End Date</label>
              <input
                type="datetime-local"
                value={caseData?.rental_end_date?.slice(0, 16) || ''}
                onChange={(e) => handleUpdateDates(caseData?.rental_start_date, e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#6563ff] focus:ring-[#6563ff]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Days</label>
              <div className="mt-2">
                <p className="text-lg font-semibold text-gray-900">
                  {caseData.total_days || 0} days
                </p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Daily Rates</label>
              <div className="mt-2 space-y-1">
                <p className="text-sm font-medium text-gray-900">
                  Rate A: ${caseData.bike?.rate_a?.toFixed(2) || '0.00'}
                </p>
                <p className="text-sm font-medium text-gray-900">
                  Rate B: ${caseData.bike?.rate_b?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Rate A</label>
              <div className="mt-2">
                <p className="text-lg font-semibold text-gray-900">
                  ${((caseData.total_days || 0) * (caseData.bike?.rate_a || 0)).toFixed(2)}
                </p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Rate B</label>
              <div className="mt-2">
                <p className="text-lg font-semibold text-gray-900">
                  ${((caseData.total_days || 0) * (caseData.bike?.rate_b || 0)).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-b border-gray-200 p-6 bg-white">
          <div className="space-y-2">
            <label className="block text-lg font-medium text-gray-900 mb-2">
              Case Status
            </label>
            <div className={`relative p-4 rounded-xl bg-gradient-to-b from-orange-50 to-orange-100/50 shadow-[0_0_0_2px_#fdba74,0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1),inset_0_1px_1px_rgba(255,255,255,0.6)] transition-all duration-200 ${statusError ? 'shake animate-shake' : ''}`}>
              <select
                value={caseData?.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="block w-full text-lg px-4 py-3 pr-10 bg-transparent border-2 border-orange-200/50 rounded-lg focus:border-[#6563ff] focus:ring focus:ring-[#6563ff]/20 appearance-none transition-colors font-medium"
              >
                {STATUS_ORDER.map((status, index) => {
                  const currentIndex = STATUS_ORDER.indexOf(caseData.status);
                  const isCurrentStatus = status === caseData.status;
                  const isNextStatus = index === currentIndex + 1;
                  const isDisabled = !isNextStatus && !isCurrentStatus;
                  
                  return (
                    <option
                      key={status}
                      value={status}
                      disabled={isDisabled}
                      className={`
                        py-2 px-4 text-base font-medium
                        ${isCurrentStatus ? 'bg-orange-100 text-orange-900' : ''}
                        ${isNextStatus ? 'bg-green-100 text-green-900' : ''}
                        ${isDisabled ? 'text-gray-400' : 'text-gray-900'}
                      `}
                    >
                      {formatStatus(status)}
                      {isCurrentStatus ? ' (Current)' : ''}
                      {isNextStatus ? ' (Next Available)' : ''}
                    </option>
                  );
                })}
              </select>
              <div className="absolute inset-y-0 right-4 flex items-center px-2 pointer-events-none">
                <ChevronDown className="h-6 w-6 text-orange-500" />
              </div>
            </div>
            {statusError && (
              <div className="p-4 bg-red-50 border border-red-100 rounded-lg mt-2">
                <p className="text-red-700 font-medium">{statusError}</p>
              </div>
            )}
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-6">
            {/* Financial Details Section - Moved above notes */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <h2 className="text-xl font-semibold mb-6">Financial Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <AmountInput
                  label="Invoiced Amount"
                  value={caseData?.invoiced_amount?.toString() || '0.00'}
                  onChange={async (value) => {
                    try {
                      const { error } = await supabase
                        .from('cases')
                        .update({ invoiced_amount: parseFloat(value) })
                        .eq('id', id);
                      if (error) throw error;
                      fetchCaseData();
                    } catch (error) {
                      console.error('Error updating invoiced amount:', error);
                    }
                  }}
                  increment={500}
                />
                
                <AmountInput
                  label="Reserve Amount"
                  value={caseData?.reserve_amount?.toString() || '0.00'}
                  onChange={async (value) => {
                    try {
                      const { error } = await supabase
                        .from('cases')
                        .update({ reserve_amount: parseFloat(value) })
                        .eq('id', id);
                      if (error) throw error;
                      fetchCaseData();
                    } catch (error) {
                      console.error('Error updating reserve amount:', error);
                    }
                  }}
                  increment={500}
                />
                
                <AmountInput
                  label="Settlement Amount"
                  value={caseData?.settlement_amount?.toString() || '0.00'}
                  onChange={async (value) => {
                    try {
                      const { error } = await supabase
                        .from('cases')
                        .update({ settlement_amount: parseFloat(value) })
                        .eq('id', id);
                      if (error) throw error;
                      fetchCaseData();
                    } catch (error) {
                      console.error('Error updating settlement amount:', error);
                    }
                  }}
                  increment={500}
                />
                
                <AmountInput
                  label="Paid Amount"
                  value={caseData?.paid_amount?.toString() || '0.00'}
                  onChange={async (value) => {
                    try {
                      const { error } = await supabase
                        .from('cases')
                        .update({
                          paid_amount: parseFloat(value),
                          payment_date: value > 0 ? new Date().toISOString() : null
                        })
                        .eq('id', id);
                      if (error) throw error;
                      fetchCaseData();
                    } catch (error) {
                      console.error('Error updating paid amount:', error);
                    }
                  }}
                  increment={500}
                />
              </div>
            </div>

            {/* Notes Section */}
            <div className="flex flex-col">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note..."
                className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6563ff] focus:border-[#6563ff] whitespace-pre-wrap resize-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    handleAddNote();
                  }
                }}
              />
              <button
                onClick={handleAddNote}
                className="self-end mt-2 px-6 py-2 bg-[#6563ff] text-white rounded-lg hover:bg-[#5451ff] transition-colors"
              >
                Save Note
              </button>
            </div>

            <div className="space-y-3 max-h-[600px] overflow-y-auto rounded-lg border border-gray-200">
              {caseData?.notes?.map((note, index) => (
                <div 
                  key={index} 
                  className={`p-4 border-b last:border-b-0 ${
                    note.type === 'status_change' ? 'bg-blue-50' : 'bg-white'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <p className="text-gray-800 whitespace-pre-wrap flex-1 mr-4">{note.content}</p>
                    <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                      {editingNote === index ? (
                        <input
                          type="datetime-local"
                          defaultValue={note.timestamp.slice(0, 16)}
                          onChange={(e) => handleUpdateNoteTimestamp(index, e.target.value)}
                          className="text-sm border border-gray-300 rounded px-2 py-1"
                        />
                      ) : (
                        <span className="text-sm text-gray-500 whitespace-nowrap">
                          {new Date(note.timestamp).toLocaleString()}
                        </span>
                      )}
                      <button
                        onClick={() => setEditingNote(editingNote === index ? null : index)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <Calendar className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  {note.type === 'status_change' && (
                    <p className="text-sm text-blue-600 mt-1">
                      Status changed from {formatStatus(note.from)} to {formatStatus(note.to)}
                    </p>
                  )}
                </div>
              ))}
              {(!caseData?.notes || caseData.notes.length === 0) && (
                <div className="text-center py-8">
                  <p className="text-gray-500">No notes yet. Add your first note above.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showReserveInput && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Enter Reserve Amount</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reserve Amount ($)
                </label>
                <AmountInput
                  value={reserveAmount}
                  onChange={setReserveAmount}
                  increment={500}
                />
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowReserveInput(false)}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReserveAmountSubmit}
                  className="px-4 py-2 bg-[#6563ff] text-white rounded-lg hover:bg-[#5451ff] transition-colors"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSettlementInput && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Enter Settlement Amount</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Settlement Amount ($)
                </label>
                <AmountInput
                  value={settlementAmount}
                  onChange={setSettlementAmount}
                  increment={500}
                />
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowSettlementInput(false)}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSettlementAmountSubmit}
                  className="px-4 py-2 bg-[#6563ff] text-white rounded-lg hover:bg-[#5451ff] transition-colors"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showReturnConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">End Rental Period</h2>
            <p className="text-gray-700 mb-6">
              Are you sure you want to mark this bike as returned? This will end the rental period and update the final amount.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowReturnConfirmation(false)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReturn}
                className="px-4 py-2 bg-[#6563ff] text-white rounded-lg hover:bg-[#5451ff] transition-colors"
              >
                Confirm Return
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Delete Case</h2>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete case number {caseData.case_number}? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteConfirmation(false)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCase}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Case
              </button>
            </div>
          </div>
        </div>
      )}

      {showPhotoCapture && (
        <PhotoCapture
          onDataExtracted={handlePhotoData}
          onClose={() => setShowPhotoCapture(false)}
        />
      )}
    </div>
  );
}

function formatStatus(status: string | null): string {
  if (!status) return 'Unknown Status';
  
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}